import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { SalariesTieOut, TieOutFigure } from '../../salariesMeta';

type TieOutBarProps = {
    /** Null while the chart of accounts is still loading. */
    tieOut: SalariesTieOut | null;
};

const Side: React.FC<{
    label: string;
    accountsLabel: string;
    figure: TieOutFigure;
    differenceLabel: string;
    unavailableLabel: string;
}> = ({ label, accountsLabel, figure, differenceLabel, unavailableLabel }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        {figure.isResolved ? (
            <>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span
                        className="text-base font-semibold tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `salaries-tie-out-accrued-${figure.slot}`,
                        )}
                    >
                        {formatGel(figure.accruedTetri)}
                    </span>
                    <Icon
                        aria-hidden
                        className="size-3.5 shrink-0 text-muted"
                        icon={
                            figure.matches
                                ? 'gravity-ui:equal'
                                : 'gravity-ui:arrow-right-arrow-left'
                        }
                    />
                    <span
                        className="text-base font-semibold tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `salaries-tie-out-ledger-${figure.slot}`,
                        )}
                    >
                        {formatGel(figure.ledgerTetri)}
                    </span>
                </div>
                <span className="text-xs text-muted">{accountsLabel}</span>
                {figure.matches ? null : (
                    <span
                        className="text-xs font-medium text-foreground"
                        {...getNodeTestIdProp(
                            `salaries-tie-out-difference-${figure.slot}`,
                        )}
                    >
                        {differenceLabel}
                    </span>
                )}
            </>
        ) : (
            // No account tagged for this role, or one the loaded chart does not
            // hold. Stating that is the honest answer; drawing a difference
            // equal to the whole accrual would not be.
            <span
                className="text-xs text-muted"
                {...getNodeTestIdProp(
                    `salaries-tie-out-unavailable-${figure.slot}`,
                )}
            >
                {unavailableLabel}
            </span>
        )}
    </div>
);

/**
 * What payroll has **accrued**, beside what the ledger holds.
 *
 * A first-class element, not a debug aid. The employee register is a subsidiary
 * ledger: every figure on the left comes from run lines whose journal entry has
 * actually posted, and every figure on the right is the account's own balance,
 * read from the same chart-of-accounts feed every other accounting screen uses.
 * The register names the accounts; it never supplies their balances.
 *
 * **A difference is not automatically an error, and this deliberately does not
 * call it one.** Paying employees and remitting withholdings are ordinary
 * journal entries the register never learns about, so a payment moves the ledger
 * down while the accrued figure stays where it was. That gap is the payments —
 * or a mis-posting — and reading it is the point. It is shown in the foreground
 * colour with a neutral summary, not in danger red, because a screen that cries
 * "the books disagree" at every settled payroll teaches people to ignore it.
 *
 * While the chart is loading it says so rather than drawing a difference — an
 * unknown ledger is not a disagreeing one.
 */
export const TieOutBar: React.FC<TieOutBarProps> = ({ tieOut }) => {
    const { t } = useTranslation('pages');

    if (tieOut === null) {
        return (
            <div
                className="flex items-center gap-2 rounded-xl bg-surface-secondary px-4 py-3 text-sm text-muted"
                {...getNodeTestIdProp('salaries-tie-out-loading')}
            >
                <Icon aria-hidden icon="gravity-ui:circle-info" />
                {t('salaries.tieOut.loading')}
            </div>
        );
    }

    // Not one of the three payroll roles is tagged in the chart, so there is no
    // ledger side at all to compare against.
    if (!tieOut.isResolved) {
        return (
            <div
                className="flex items-center gap-2 rounded-xl bg-surface-secondary px-4 py-3 text-sm text-muted"
                {...getNodeTestIdProp('salaries-tie-out-pending')}
            >
                <Icon aria-hidden icon="gravity-ui:circle-info" />
                {t('salaries.tieOut.noAccounts')}
            </div>
        );
    }

    return (
        <section
            aria-label={t('salaries.tieOut.title')}
            className="flex flex-col gap-4 rounded-xl bg-surface-secondary px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
            {...getNodeTestIdProp('salaries-tie-out')}
        >
            <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
                {tieOut.figures.map((figure) => (
                    <Side
                        accountsLabel={t('salaries.tieOut.against', {
                            account: figure.accountCode,
                        })}
                        differenceLabel={t('salaries.tieOut.offBy', {
                            amount: formatGel(figure.differenceTetri),
                        })}
                        figure={figure}
                        key={figure.slot}
                        label={t(figure.labelKey)}
                        unavailableLabel={t('salaries.tieOut.noAccount')}
                    />
                ))}
            </div>

            <div
                className={cn(
                    'flex w-fit items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold',
                    tieOut.inAgreement
                        ? 'bg-success-soft text-success-soft-foreground'
                        : 'bg-surface text-foreground',
                )}
                {...getNodeTestIdProp('salaries-tie-out-indicator')}
            >
                <Icon
                    aria-hidden
                    icon={
                        tieOut.inAgreement
                            ? 'gravity-ui:circle-check'
                            : 'gravity-ui:scales'
                    }
                />
                {tieOut.inAgreement
                    ? t('salaries.tieOut.inAgreement')
                    : t('salaries.tieOut.outOfAgreement')}
            </div>
        </section>
    );
};
